import { useCallback, useEffect, useRef, type MouseEvent } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import {
  NavigationMenu,
  ArrowRightFromBracketIcon,
  ClockIcon,
  GearIcon,
  IdCardIcon,
  ListIcon,
  PrescriptionIcon,
  SitemapIcon,
} from '@quickmd-llc/design-system'
import { LOGOUT } from '../../app/authState'
import { getAdminProfile } from '../../store/accountSettings/AccountSettingsThunk'
import { setTabs } from '../../store/accountSettings/AccountSettingsActions'
import { sendCustomSentryMessage } from '../../utils/sentryUtility'
import {
  ACCOUNT_SETTINGS_ROUTE,
  AVAILABILITY_ROUTE,
  HOME_PAGE_ROUTE,
  MANAGEMENT_PAGE_ROUTE,
  MEDICATIONS_PAGE_ROUTE,
  PATIENT_CHARTS_PAGE_ROUTE,
  PRINTED_FRIENDLY_PATIENT_CHARTS,
  VISITS_PAGE_ROUTE,
} from '../../utils/constants'
import { AppLogout } from '../../pages/home/AppLogout/AppLogout'
import { useVisitCancellationReasons } from '../CancelVisitPopup/useVisitCancellationReasons'
import { useAppSelector } from '@/redux/hooks/useAppSelector'
import { NavigationGuardProvider } from './navigationGuard/NavigationGuardProvider'
import { useNavigationGuardRef } from './navigationGuard/useNavigationGuard'

const NAV_ITEMS = [
  { id: 'management', icon: ListIcon, label: 'Management', href: MANAGEMENT_PAGE_ROUTE },
  { id: 'visits', icon: SitemapIcon, label: 'Visits', href: VISITS_PAGE_ROUTE },
  { id: 'availability', icon: ClockIcon, label: 'Availability', href: AVAILABILITY_ROUTE },
  { id: 'patient-charts', icon: IdCardIcon, label: 'Patient charts', href: PATIENT_CHARTS_PAGE_ROUTE },
  { id: 'medications', icon: PrescriptionIcon, label: 'Medications', href: MEDICATIONS_PAGE_ROUTE },
]

function AuthenticatedLayoutInner() {
  const dispatch = useDispatch()
  const isLoggedIn = useAppSelector((state) => state.auth.accessToken)
  const adminProfile = useAppSelector((state) => state.accountSettings.adminProfile)
  const isEditSuccess = useAppSelector((state) => state.accountSettings.isEditSuccess)

  const onLogout = useCallback(() => {
    dispatch({ type: LOGOUT })
    sendCustomSentryMessage('Logout sidebar clicked')
    dispatch(setTabs())
  }, [dispatch])

  // Warm the visit-cancellation reason codes once on login so the cancel popup opens with the
  // list already cached (no loading state needed).
  useVisitCancellationReasons()

  useEffect(() => {
    if (!adminProfile?.Email) {
      const profileAction = getAdminProfile()
      if (profileAction) dispatch(profileAction)
    }
  }, [dispatch, isEditSuccess, adminProfile?.Email])

  const footerItems = [
    { id: 'settings', icon: GearIcon, label: 'Settings', href: ACCOUNT_SETTINGS_ROUTE },
    { id: 'logout', icon: ArrowRightFromBracketIcon, label: 'Sign out', onClick: onLogout },
  ]

  // Keep --nav-width in sync with the sidebar's actual rendered width so that
  // position:fixed action bars can use left:var(--nav-width) without guessing.
  const navRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    const nav = document.querySelector<HTMLElement>('nav[aria-label="Main navigation"]')
    if (!nav) return
    navRef.current = nav
    const apply = () =>
      document.documentElement.style.setProperty('--nav-width', `${nav.getBoundingClientRect().width}px`)
    apply()
    const ro = new ResizeObserver(apply)
    ro.observe(nav)
    return () => ro.disconnect()
  }, [])

  const location = useLocation()
  const hideNav = location.pathname.startsWith(PRINTED_FRIENDLY_PATIENT_CHARTS)

  // A page (e.g. state settings) can register a guard that intercepts nav-bar navigations when it
  // has unsaved changes. NavLink honours preventDefault, so blocking the click cancels the route
  // change and lets the page show its own confirmation popup.
  const guardRef = useNavigationGuardRef()
  const makeGuardedOnClick = (href?: string) => (event: MouseEvent) => {
    if (href && guardRef.current?.(href)) event.preventDefault()
  }

  const navItems = NAV_ITEMS.map((item) => ({ ...item, onClick: makeGuardedOnClick(item.href) }))
  const footerNavItems = footerItems.map((item) =>
    'href' in item ? { ...item, onClick: makeGuardedOnClick(item.href) } : item,
  )

  if (!isLoggedIn) {
    return <Navigate to={HOME_PAGE_ROUTE} replace />
  }

  if (hideNav) {
    return (
      <AppLogout>
        <Outlet />
      </AppLogout>
    )
  }

  return (
    <AppLogout>
      <NavigationMenu items={navItems} footerItems={footerNavItems} showMobileNav={false}>
        <div className='flex min-h-full flex-1 flex-col bg-white'>
          <Outlet />
        </div>
      </NavigationMenu>
    </AppLogout>
  )
}

export function AuthenticatedLayout() {
  return (
    <NavigationGuardProvider>
      <AuthenticatedLayoutInner />
    </NavigationGuardProvider>
  )
}
